/**
 * DesignSectionsMenu.tsx - Section Visibility and Management Component
 *
 * This component provides controls for managing section visibility and
 * arrangement in the fundraiser layout with toggle switches and organization.
 *
 * Key Features:
 * - Section visibility toggle switches
 * - Organized section categories and grouping
 * - Visual indicators for section status
 * - Bulk section management options
 * - Integration with layout zone assignments
 * - Real-time preview updates
 *
 * Section Categories:
 * - Core Sections: Essential fundraiser components
 * - Progress Sections: Fundraiser and promoter progress
 * - Content Sections: Mission, impact, media, stats
 * - Interactive Sections: Donation form, CTA, leaderboard
 * - Additional Sections: Recent donations, sponsor banner
 *
 * Management Features:
 * - Individual section visibility toggles
 * - Show/hide all sections bulk operations
 * - Section status indicators (visible/hidden)
 * - Integration with drag-and-drop layout system
 *
 * Props: None (uses MobX store and design constants)
 *
 * Dependencies: Available sections, visibility management, MobX store
 */

import React, { useEffect } from "react";
import styled from "styled-components";
import { Eye, EyeOff, ChevronUp, ChevronDown } from "lucide-react";
import { availableSections } from "./designConstants";
import { handleSectionVisibilityChange } from "./DropZone";
import { fundraiserStore } from "../../../stores/FundraiserStore";
import { DonationFormType, FundRaiserTemplateLayoutType } from "../../../enums";
import { ISectionVisibility } from "../../../interfaces";
import { CreateFundraiserModel } from "../../../models";

const SubMenuContent = styled.div`
  padding: 8px 12px 12px 12px;
  background: #ffffff;
`;

const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
  font-size: 11px;
`;

const SectionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  min-height: 28px;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
  &.section-container {
  }
`;

const SectionItemWithCounter = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 8px;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  min-height: 58px;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const SectionTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const SectionBottomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 9px;
`;

const SectionName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
`;

const CounterLabel = styled.div`
  font-size: 9px;
  color: #64748b;
  font-weight: 500;
`;

const CounterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CounterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  border-radius: 0;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #1e293b;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CounterValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  min-width: 16px;
  text-align: center;
`;

const ToggleButton = styled.button<{ active: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${(props) => (props.active ? "#3b82f6" : "#9ca3af")};
  cursor: pointer;
  transition: all 0.2s ease;

  ${(props) =>
    props.disabled
      ? `
    cursor: not-allowed;
  `
      : `
    cursor: pointer;
  `}

  &:hover {
    background: transparent;
  }
  &.section-toggle-btn {
    height: 28px;
    width: 28px;
  }
`;

const DarkCheckbox = styled.input`
  width: 14px;
  height: 14px;
  cursor: pointer;
  accent-color: #374151;
`;

const PromoterCaption = styled.div`
  font-size: 10px;
  color: #67737e;
  line-height: 1.3;
  margin-top: 6px;
  margin-bottom: 6px;
  padding: 0 2px;
  max-width: 100%;
`;

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: sticky;
  top: 32px;
  height: calc(100vh - 64px);
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f8fafc;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 3px;
  }
`;

interface DesignSectionsMenuProps {
  sectionVisibility: ISectionVisibility;
  promoterCount: number;
  recentDonationsCount: number;
  showDonationComments: boolean;
  progressLayout: string;
  onSectionToggle: (sectionId: string) => void;
  onPromoterCountChange: (increment: boolean) => void;
  onRecentDonationsCountChange: (increment: boolean) => void;
  onShowCommentsToggle: () => void;
  onProgressFundraiserToggle: () => void;
  onProgressPromoterToggle: () => void;
  onProgressLayoutChange: (layout: "side-by-side" | "stacked") => void;
  builderData?: CreateFundraiserModel;
}

export const DesignSectionsMenu: React.FC<DesignSectionsMenuProps> = ({
  sectionVisibility,
  promoterCount,
  recentDonationsCount,
  showDonationComments,
  progressLayout,
  onSectionToggle,
  onPromoterCountChange,
  onRecentDonationsCountChange,
  onShowCommentsToggle,
  onProgressFundraiserToggle,
  onProgressPromoterToggle,
  onProgressLayoutChange,
  builderData,
}) => {
  // Get current layout for section placement logic
  const currentLayoutId =
    builderData?.selectedLayout || FundRaiserTemplateLayoutType.Layout1;

  // Auto-manage visibility based on content availability
  useEffect(() => {
    const hasDescriptionContent = !!(
      builderData?.description && builderData.description.trim()
    );
    const hasSponsorContent = !!builderData?.bannerImage;

    // Auto-hide description if no content and currently visible
    if (!hasDescriptionContent && sectionVisibility.description !== false) {
      const updated = { ...sectionVisibility, description: false };
      fundraiserStore.updateBuilderField("sectionVisibility", updated);
    }

    // Auto-hide sponsor if no content and currently visible
    if (!hasSponsorContent && sectionVisibility.sponsor !== false) {
      const updated = { ...sectionVisibility, sponsor: false };
      fundraiserStore.updateBuilderField("sectionVisibility", updated);
    }
  }, [
    builderData?.description,
    builderData?.bannerImage,
    sectionVisibility.description,
    sectionVisibility.sponsor,
  ]);

  // Enhanced section toggle that handles automatic placement
  const handleSectionToggleWithPlacement = (sectionId: string) => {
    const currentVisibility = sectionVisibility[sectionId] !== false;
    const newVisibility = !currentVisibility;

    // Call original toggle function
    onSectionToggle(sectionId);

    // Handle automatic placement for newly visible sections
    handleSectionVisibilityChange(sectionId, newVisibility, currentLayoutId);
  };

  // Helper function to render section items with explicit height logic
  const renderSectionItem = (section: any) => {
    const isVisible = sectionVisibility[section.id];

    // Check if description content exists
    const hasDescriptionContent = () => {
      return !!(builderData?.description && builderData.description.trim());
    };

    // Check if sponsor content exists
    const hasSponsorContent = () => {
      return builderData?.bannerImage.fullURL;
    };

    // Skip description section if no description content exists
    if (section.id === "description" && !hasDescriptionContent()) {
      return null;
    }

    // Skip sponsor section if no sponsor content exists
    if (section.id === "sponsor" && !hasSponsorContent()) {
      return null;
    }

    if (section.id === "impact" && builderData?.impact.length === 0) {
      return null;
    }

    if (section.id === "stats" && builderData?.fundraiserStats.length === 0) {
      return null;
    }

    if (
      section.id === "mission" &&
      builderData?.missionTitle.trim() === "" &&
      builderData?.missionDescription.trim() === ""
    ) {
      return null;
    }

    if (section.id === "progress") {
      // Handle the new combined Progress section
      const isFundraiserVisible =
        sectionVisibility.fundraiserProgress !== false;
      const isPromoterVisible = sectionVisibility.promoterProgress !== false;
      const showRadioButtons = isFundraiserVisible && isPromoterVisible;

      return (
        <SectionItemWithCounter
          key={section.id}
          style={{
            minHeight: showRadioButtons
              ? isPromoterVisible
                ? "118px"
                : "98px"
              : isPromoterVisible
                ? "98px"
                : "78px",
          }}
        >
          <SectionTopRow>
            <SectionName>{section.name}</SectionName>
            <ToggleButton
              active={isFundraiserVisible || isPromoterVisible}
              onClick={() => {
                const currentlyVisible =
                  isFundraiserVisible || isPromoterVisible;
                const newValue = !currentlyVisible;

                if (newValue) {
                  // When making progress visible, explicitly set both to true
                  const updated = {
                    ...sectionVisibility,
                    fundraiserProgress: true,
                    promoterProgress: true,
                  };
                  // Update the store directly instead of using toggle functions
                  fundraiserStore.updateBuilderField(
                    "sectionVisibility",
                    updated
                  );
                } else {
                  // When hiding progress, explicitly set both to false
                  const updated = {
                    ...sectionVisibility,
                    fundraiserProgress: false,
                    promoterProgress: false,
                  };
                  // Update the store directly instead of using toggle functions
                  fundraiserStore.updateBuilderField(
                    "sectionVisibility",
                    updated
                  );
                }

                // Handle placement for progress sections
                if (newValue) {
                  handleSectionVisibilityChange(
                    "fundraiserProgress",
                    true,
                    currentLayoutId
                  );
                  handleSectionVisibilityChange(
                    "promoterProgress",
                    true,
                    currentLayoutId
                  );
                } else {
                  handleSectionVisibilityChange(
                    "fundraiserProgress",
                    false,
                    currentLayoutId
                  );
                  handleSectionVisibilityChange(
                    "promoterProgress",
                    false,
                    currentLayoutId
                  );
                }
              }}
              className="section-toggle-btn"
            >
              {isFundraiserVisible || isPromoterVisible ? (
                <Eye size={20} />
              ) : (
                <EyeOff size={20} />
              )}
            </ToggleButton>
          </SectionTopRow>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              paddingTop: "4px",
            }}
          >
            {/* Fundraiser and Promoter checkboxes on same row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  minWidth: "120px",
                }}
              >
                <DarkCheckbox
                  type="checkbox"
                  checked={isFundraiserVisible}
                  onChange={() => {
                    const newFundraiserVisible = !isFundraiserVisible;
                    const currentPromoterVisible =
                      sectionVisibility.promoterProgress !== false;

                    // Update visibility state
                    const updated = {
                      ...sectionVisibility,
                      fundraiserProgress: newFundraiserVisible,
                    };
                    fundraiserStore.updateBuilderField(
                      "sectionVisibility",
                      updated
                    );

                    // Handle zone placement
                    handleSectionVisibilityChange(
                      "fundraiserProgress",
                      newFundraiserVisible,
                      currentLayoutId
                    );

                    // If both sections become hidden, remove from all zones
                    if (!newFundraiserVisible && !currentPromoterVisible) {
                      handleSectionVisibilityChange(
                        "fundraiserProgress",
                        false,
                        currentLayoutId
                      );
                      handleSectionVisibilityChange(
                        "promoterProgress",
                        false,
                        currentLayoutId
                      );
                    }
                  }}
                />
                <CounterLabel style={{ fontSize: "14px" }}>
                  Fundraiser
                </CounterLabel>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  minWidth: "100px",
                }}
              >
                <DarkCheckbox
                  type="checkbox"
                  checked={isPromoterVisible}
                  onChange={() => {
                    const newPromoterVisible = !isPromoterVisible;
                    const currentFundraiserVisible =
                      sectionVisibility.fundraiserProgress !== false;

                    // Update visibility state
                    const updated = {
                      ...sectionVisibility,
                      promoterProgress: newPromoterVisible,
                    };
                    fundraiserStore.updateBuilderField(
                      "sectionVisibility",
                      updated
                    );

                    // Handle zone placement
                    handleSectionVisibilityChange(
                      "promoterProgress",
                      newPromoterVisible,
                      currentLayoutId
                    );

                    // If both sections become hidden, remove from all zones
                    if (!newPromoterVisible && !currentFundraiserVisible) {
                      handleSectionVisibilityChange(
                        "fundraiserProgress",
                        false,
                        currentLayoutId
                      );
                      handleSectionVisibilityChange(
                        "promoterProgress",
                        false,
                        currentLayoutId
                      );
                    }
                  }}
                />
                <CounterLabel style={{ fontSize: "14px" }}>
                  Promoter
                </CounterLabel>
              </div>
            </div>

            {/* Radio buttons for layout - only show if both are selected */}
            {showRadioButtons && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "20px",
                  marginTop: "0px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    minWidth: "120px",
                  }}
                >
                  <input
                    type="radio"
                    name="progressLayout"
                    checked={progressLayout === "side-by-side"}
                    onChange={() => onProgressLayoutChange("side-by-side")}
                    style={{
                      width: "12px",
                      height: "12px",
                      accentColor: "#374151",
                    }}
                  />
                  <CounterLabel style={{ fontSize: "14px" }}>
                    Side-by-side
                  </CounterLabel>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    minWidth: "100px",
                  }}
                >
                  <input
                    type="radio"
                    name="progressLayout"
                    checked={progressLayout === "stacked"}
                    onChange={() => onProgressLayoutChange("stacked")}
                    style={{
                      width: "12px",
                      height: "12px",
                      accentColor: "#374151",
                    }}
                  />
                  <CounterLabel style={{ fontSize: "14px" }}>
                    Stacked
                  </CounterLabel>
                </div>
              </div>
            )}

            {/* Caption for Promoter section - only show if Promoter is selected */}
            {isPromoterVisible && (
              <PromoterCaption>
                Promoter progress will be added to promoter-specific donate page
                next to fundraiser progress
              </PromoterCaption>
            )}
          </div>
        </SectionItemWithCounter>
      );
    }

    // Render Leaderboard (double height with counter controls)
    if (section.id === "promoters") {
      return (
        <SectionItemWithCounter key={section.id} style={{ minHeight: "48px" }}>
          <SectionTopRow>
            <SectionName>{section.name}</SectionName>
            <ToggleButton
              active={isVisible}
              onClick={() => handleSectionToggleWithPlacement(section.id)}
              className="section-toggle-btn"
            >
              {isVisible ? <Eye size={20} /> : <EyeOff size={20} />}
            </ToggleButton>
          </SectionTopRow>
          <SectionBottomRow style={{ marginTop: "0px", paddingTop: "4px" }}>
            <CounterLabel style={{ fontSize: "14px" }}>Top</CounterLabel>
            <CounterContainer>
              <CounterButton onClick={() => onPromoterCountChange(false)}>
                <ChevronDown size={25} />
              </CounterButton>
              <CounterValue>{promoterCount}</CounterValue>
              <CounterButton onClick={() => onPromoterCountChange(true)}>
                <ChevronUp size={25} />
              </CounterButton>
            </CounterContainer>
          </SectionBottomRow>
        </SectionItemWithCounter>
      );
    }

    // Render Donations (double height with counter controls and comments)
    if (section.id === "recent") {
      return (
        <SectionItemWithCounter key={section.id} style={{ minHeight: "68px" }}>
          <SectionTopRow>
            <SectionName>{section.name}</SectionName>
            <ToggleButton
              active={isVisible}
              onClick={() => handleSectionToggleWithPlacement(section.id)}
              className="section-toggle-btn"
            >
              {isVisible ? <Eye size={20} /> : <EyeOff size={20} />}
            </ToggleButton>
          </SectionTopRow>
          <SectionBottomRow
            style={{
              alignItems: "center",
              justifyContent: "flex-start",
              gap: "16px",
              width: "100%",
              paddingTop: "4px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                minWidth: "60px",
              }}
            >
              <CounterLabel style={{ fontSize: "14px" }}>Last</CounterLabel>
              <CounterContainer>
                <CounterButton
                  onClick={() => onRecentDonationsCountChange(false)}
                >
                  <ChevronDown size={18} />
                </CounterButton>
                <CounterValue>{recentDonationsCount}</CounterValue>
                <CounterButton
                  onClick={() => onRecentDonationsCountChange(true)}
                >
                  <ChevronUp size={18} />
                </CounterButton>
              </CounterContainer>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                minWidth: "90px",
              }}
            >
              <DarkCheckbox
                type="checkbox"
                checked={showDonationComments}
                onChange={onShowCommentsToggle}
              />
              <CounterLabel style={{ fontSize: "14px" }}>Comments</CounterLabel>
            </div>
          </SectionBottomRow>
        </SectionItemWithCounter>
      );
    }

    // All other sections (single height)
    return (
      <SectionItem key={section.id} className="section-container">
        <SectionName>
          <span style={{ fontSize: "16px" }}>{section.name}</span>
        </SectionName>
        <ToggleButton
          active={isVisible}
          disabled={section.id === "donation"}
          onClick={() =>
            section.id !== "donation" &&
            handleSectionToggleWithPlacement(section.id)
          }
          className="section-toggle-btn"
        >
          {isVisible ? <Eye size={20} /> : <EyeOff size={20} />}
        </ToggleButton>
      </SectionItem>
    );
  };

  return (
    <SubMenuContent>
      <SectionsGrid>
        {availableSections
          .filter((section) => {
            if (
              section.id === "fundraiserProgress" ||
              section.id === "promoterProgress"
            ) {
              return false;
            }
            if (section.id === "donation") {
              const donationFormType =
                builderData?.donationFormType || DonationFormType.Embedded;
              return donationFormType === DonationFormType.Embedded;
            }

            return true;
          })
          .map((section) => renderSectionItem(section))
          .filter(Boolean)}
      </SectionsGrid>
    </SubMenuContent>
  );
};
